/**
 * DT One eSIM (DVS service 13) — mirrors the shapes returned by the Global API
 * under `/dtOne/esim`. Field names in `snake_case` are DT One's own and are
 * passed through by the API verbatim.
 */

export type EsimCurrency = "EUR" | "GBP" | "CAD";

/** DVS coverage buckets. `GXX` = global, `EXX` = Europe, otherwise alpha-3. */
export type EsimCoverage = "country" | "regional" | "global";

export type EsimRegion = {
  code: string;
  name: string;
};

export type EsimDestination = {
  name: string;
  isoCode: string;
  coverage: EsimCoverage;
  regions: EsimRegion[] | null;
};

export type EsimDestinations = {
  country: EsimDestination[];
  regional: EsimDestination[];
  global: EsimDestination[];
};

/** One line of a plan's allowance — data size, minutes, SMS. */
export type EsimProductBenefit = {
  type: string;
  unit: string;
  unit_type: string;
  amount: {
    base: number;
    total_including_tax: number;
  };
  additional_information: string | null;
};

export type EsimProduct = {
  id: number;
  name: string;
  description: string;
  operator: string;
  country: {
    name: string;
    isoCode: string;
    coverage: EsimCoverage;
    regions: EsimRegion[] | null;
  } | null;
  validity: { quantity: number; unit: string } | null;
  benefits: EsimProductBenefit[];
  usageInfo: string[];
  destination: { amount: number; unit: string };
  prices: {
    retail: { amount: number; fee: number; unit: string };
    wholesale: { amount: number; fee: number; unit: string };
  };
};

/**
 * The store's catalogue for one destination, and why it is empty when it is.
 *
 * `allowanceBlocked` is the API's answer, not ours: it means this destination
 * sells plans and the member's remaining allowance reaches none of them. The
 * store cannot work that out from `products` alone — an empty list looks the
 * same either way — and answering a spent plan with "try another country"
 * sends the member through every country in the list for nothing.
 */
export type EsimCatalogue = {
  products: EsimProduct[];
  allowanceBlocked: boolean;
};

/** Everything the customer needs to install the profile on a device. */
export type EsimProfile = {
  iccid: string;
  /** Full LPA string — this is the QR payload, and a bearer credential. */
  activationCode: string;
  smdpAddress: string | null;
  appleUniversalLink: string | null;
  androidUniversalLink: string | null;
  installationUrl: string | null;
};

/**
 * `CREATED` is the API's own status — the order exists but DT One has not
 * answered yet. Every other value comes from DT One.
 */
export type EsimOrderStatus = {
  id?: number;
  message: string;
  class?: { id: number; message: string };
  error?: string;
};

/** What a plan grants, echoed back by DT One on the transaction. */
export type EsimPlan = {
  size?: { amount: number; unit: string };
  validity?: { quantity: number; unit: string };
  country_set?: string;
};

export type EsimOrder = {
  esimId: string;
  type: "purchase" | "topup";
  externalId: string;
  transactionId: string | null;
  productId: number;
  productName: string;
  currency: string;
  amount: string;
  status: EsimOrderStatus | null;
  benefits: EsimPlan | null;
  iccid: string | null;
  /** Null until DT One's callback lands. Polled for after a purchase. */
  profile: EsimProfile | null;
};

/** One eSIM in the customer's list. Carries no activation code by design. */
export type EsimSummary = {
  iccid: string;
  /**
   * Wallet this eSIM was bought on. The list is not filtered by currency, so a
   * customer holding eSIMs from more than one wallet gets them all — and each
   * has to be read and recharged on its own, not on whatever the store's
   * currency picker happens to be set to.
   */
  currency: EsimCurrency;
  productName: string;
  benefits: EsimPlan | null;
  installedAt: string | null;
  activatedAt: string | null;
  purchasedAt: string;
};

/**
 * One eSIM on an eSIM customer's MemberZone home (GD-98). Two fields the
 * store's list does without: a paid order DT One has not delivered yet has no
 * ICCID and shows as Ongoing, and `applicationId` is what the Trustpilot popup
 * matches against the `?id=` of the magic link. Optional until the API sends it.
 */
export type RecentEsim = Omit<EsimSummary, "iccid"> & {
  iccid: string | null;
  applicationId?: string | null;
};

/**
 * What is left of the plan's included eSIM data this billing month (GD-87).
 *
 * Sizes are MB because the API meters in MB — the catalogue sells 500 MB plans,
 * and MB is the integer the purchase cap compares against. Divide for display.
 *
 * `percentUsed` is computed server-side from the same figures the cap uses, so
 * the bar can never disagree with the refusal a customer would hit at checkout.
 */
export type EsimUsage = {
  limitMb: number;
  usedMb: number;
  remainingMb: number;
  percentUsed: number;
  periodStart: string;
  /** When the balance refills — the renewal date the card shows. */
  periodEnd: string;
};

/** Remaining allowance. `expiration_date: null` means the plan has not started. */
export type EsimBalance = {
  type: string;
  unit_type: string;
  unit: string;
  amount: number;
  expiration_date: string | null;
};

export type EsimDetail = {
  esim: EsimProfile & {
    installedAt: string | null;
    activatedAt: string | null;
  };
  orders: EsimOrder[];
  balance: EsimBalance[] | null;
  status: {
    installation_date: string | null;
    activation_date: string | null;
  } | null;
};

/** Where an eSIM is in its life: delivered → installed on a device → using data. */
export type EsimLifecycle = "ready" | "installed" | "active";

export function esimLifecycle(
  esim: Pick<EsimSummary, "installedAt" | "activatedAt">,
): EsimLifecycle {
  if (esim.activatedAt) return "active";
  if (esim.installedAt) return "installed";
  return "ready";
}
